import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Save,
  Upload,
  BookOpen,
  AlertCircle,
  Check,
  ChevronDown,
  X,
  FileText,
  Database,
  Globe,
  Lock,
  Filter,
  Plus,
  Trash2,
  RefreshCw,
  Search
} from 'lucide-react';
import chatAgentService, {
  KnowledgeBaseDto,
  HelperKnowledgeBaseDto
} from './ChatIntents/chatAgentService';

// Type Definitions
export type Theme = 'dark' | 'light';

export interface ThemeColors {
  primary: string;
  secondary: string;
  background: string;
  surface: string;
  border: string;
  text: {
    primary: string;
    secondary: string;
    muted: string;
  };
  accent: string;
  hover: string;
  input: string;
}

export interface KnowledgeBaseSettings {
  selectedKnowledgeBases: string[];
  enableFilters: boolean;
  publicAccess: boolean;
}

export interface ChatAgentKnowledgeBaseProps {
  theme?: Theme;
  agentId: string; // Required for mapping
}

// Theme Configuration
const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: {
      primary: 'text-white',
      secondary: 'text-neutral-300',
      muted: 'text-neutral-400'
    },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-800',
    input: 'bg-neutral-700 border-neutral-600 text-white'
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: {
      primary: 'text-gray-900',
      secondary: 'text-gray-800',
      muted: 'text-gray-700'
    },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900'
  }
};

// Knowledge Base Multi-Select Component with filter
const KnowledgeBaseSelector: React.FC<{
  availableKnowledgeBases: HelperKnowledgeBaseDto[];
  selectedKnowledgeBases: string[];
  onKnowledgeBaseToggle: (id: string) => void;
  theme: Theme;
}> = ({ availableKnowledgeBases, selectedKnowledgeBases, onKnowledgeBaseToggle, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const colors = THEME_CONFIG[theme];

  const getTypeIcon = () => {
    return <FileText className="w-4 h-4" />;
  };

  const query = filterQuery.trim().toLowerCase();
  const filteredList = query
    ? availableKnowledgeBases.filter(
      (kb) =>
        (kb.title || '').toLowerCase().includes(query) ||
        (kb.description || '').toLowerCase().includes(query) ||
        (kb.collectionNamespace || '').toLowerCase().includes(query)
    )
    : availableKnowledgeBases;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full ${colors.input} border rounded-md px-3 py-2 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm`}
      >
        <span>
          {selectedKnowledgeBases.length === 0
            ? 'Select knowledge bases...'
            : `${selectedKnowledgeBases.length} knowledge base(s) selected`
          }
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={`absolute z-10 w-full mt-1 ${colors.surface} border ${colors.border} rounded-md shadow-lg overflow-hidden`}>
          <div className={`p-2 border-b ${colors.border} sticky top-0 ${colors.primary}`}>
            <div className={`relative flex items-center`}>
              <Search className={`absolute left-2.5 w-4 h-4 ${colors.text.muted} pointer-events-none`} />
              <input
                type="text"
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                placeholder="Filter by name, description..."
                className={`w-full ${colors.input} border ${colors.border} rounded-md pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                onClick={(e) => e.stopPropagation()}
              />
              {filterQuery && (
                <button
                  type="button"
                  onClick={() => setFilterQuery('')}
                  className={`absolute right-2 p-0.5 rounded opacity-70 hover:opacity-100 ${colors.text.muted}`}
                  aria-label="Clear filter"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-72 overflow-auto">
            {availableKnowledgeBases.length === 0 ? (
              <div className="px-3 py-6 text-center">
                <p className={`text-sm ${colors.text.muted}`}>No knowledge bases available</p>
              </div>
            ) : filteredList.length === 0 ? (
              <div className="px-3 py-6 text-center">
                <p className={`text-sm ${colors.text.muted}`}>No knowledge bases match &quot;{filterQuery}&quot;</p>
              </div>
            ) : (
              filteredList.map((kb) => (
                <label
                  key={kb.id}
                  className={`flex items-start px-3 py-3 ${colors.hover} cursor-pointer border-b ${colors.border} last:border-b-0`}
                >
                  <input
                    type="checkbox"
                    checked={selectedKnowledgeBases.includes(kb.id)}
                    onChange={() => onKnowledgeBaseToggle(kb.id)}
                    className="rounded border-gray-300 text-indigo-600 mr-3 mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`${colors.text.muted}`}>
                        {getTypeIcon()}
                      </span>
                      <span className={`text-sm font-medium ${colors.text.primary}`}>{kb.title}</span>
                      <div className="flex items-center gap-1 ml-auto">
                        <Database className="w-3 h-3 text-blue-500" />
                        <span className={`text-xs ${colors.text.muted}`}>{kb.documentCount} docs</span>
                      </div>
                    </div>
                    <p className={`text-xs ${colors.text.muted} mb-1`}>{kb.description || 'No description'}</p>
                    <div className={`text-xs ${colors.text.muted} flex items-center gap-3`}>
                      <span>{kb.collectionNamespace}</span>
                      <span>Created: {new Date(kb.createdOn).toLocaleDateString()}</span>
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Toggle Switch Component
const ToggleSwitch: React.FC<{
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  theme: Theme;
  label: string;
  description?: string;
}> = ({ enabled, onChange, theme, label, description }) => {
  const colors = THEME_CONFIG[theme];

  return (
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <label className={`text-sm font-medium ${colors.text.primary} cursor-pointer`}>
          {label}
        </label>
        {description && (
          <p className={`text-xs ${colors.text.muted} mt-1`}>{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${enabled ? 'bg-indigo-600' : `${theme === 'light' ? 'bg-gray-200' : 'bg-neutral-600'}`
          }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
        />
      </button>
    </div>
  );
};

// Main Knowledge Base Component
const ChatAgentKnowledgeBase: React.FC<ChatAgentKnowledgeBaseProps> = ({
  theme = 'dark',
  agentId
}) => {
  const colors = THEME_CONFIG[theme];
  const { chatbotId } = useParams<{ chatbotId?: string }>();

  // Use chatbotId from route params if available, otherwise use prop
  const effectiveAgentId = chatbotId || agentId;

  const [settings, setSettings] = useState<KnowledgeBaseSettings>({
    selectedKnowledgeBases: [],
    enableFilters: true,
    publicAccess: false
  });

  const [availableKnowledgeBases, setAvailableKnowledgeBases] = useState<HelperKnowledgeBaseDto[]>([]);
  const [agentKnowledgeBases, setAgentKnowledgeBases] = useState<KnowledgeBaseDto[]>([]);
  const [isLoadingAvailable, setIsLoadingAvailable] = useState(true);
  const [isLoadingAgent, setIsLoadingAgent] = useState(true);
  const [removeConfirmKb, setRemoveConfirmKb] = useState<HelperKnowledgeBaseDto | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Load available knowledge bases
  useEffect(() => {
    loadAvailableKnowledgeBases();
  }, []);

  // Load agent's current knowledge bases
  useEffect(() => {
    if (effectiveAgentId) {
      loadAgentKnowledgeBases();
    }
  }, [effectiveAgentId]);

  const loadAvailableKnowledgeBases = async () => {
    try {
      setIsLoadingAvailable(true);
      const response = await chatAgentService.getKnowledgeBases();

      if (response?.success && response?.data) {
        setAvailableKnowledgeBases(response.data);
      }
    } catch (error) {
      console.error('Error loading available knowledge bases:', error);
      setSaveError('Failed to load available knowledge bases');
    } finally {
      setIsLoadingAvailable(false);
    }
  };

  const loadAgentKnowledgeBases = async () => {
    try {
      setIsLoadingAgent(true);
      const response = await chatAgentService.getKnowledgeBasesByAgent(effectiveAgentId);

      if (response?.success && response?.data) {
        const agentKBs = response.data;
        setAgentKnowledgeBases(agentKBs);

        // Set selected knowledge bases from agent's current mappings
        const selectedIds = agentKBs.map((kb: any) => kb.knowledge_base_id);
        setSettings(prev => ({
          ...prev,
          selectedKnowledgeBases: selectedIds,
          enableFilters: agentKBs[0]?.enable_filters ?? true,
          publicAccess: agentKBs[0]?.public_access ?? false
        }));
      }
    } catch (error) {
      console.error('Error loading agent knowledge bases:', error);
    } finally {
      setIsLoadingAgent(false);
    }
  };

  const handleKnowledgeBaseToggle = (id: string) => {
    setSettings(prev => ({
      ...prev,
      selectedKnowledgeBases: prev.selectedKnowledgeBases.includes(id)
        ? prev.selectedKnowledgeBases.filter(kbId => kbId !== id)
        : [...prev.selectedKnowledgeBases, id]
    }));
    setSaveSuccess(false);
  };

  const handleToggleChange = (field: 'enableFilters' | 'publicAccess', value: boolean) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!effectiveAgentId) {
      setSaveError('Agent ID is required. Please ensure you are on a valid chatbot route.');
      return;
    }

    try {
      setIsSaving(true);
      setSaveError(null);

      // Delete existing mappings first (if any)
      for (const existingKB of agentKnowledgeBases) {
        if (existingKB.id) {
          await chatAgentService.deleteEntity({
            id: existingKB.id,
            entity_type: 'knowledge_base'
          });
        }
      }

      // Create new mappings for selected knowledge bases
      for (const kbId of settings.selectedKnowledgeBases) {
        const payload: KnowledgeBaseDto = {
          knowledge_base_id: kbId,
          agent_id: effectiveAgentId, // Use route ID (chatbotId) as agentId
          kb_name: availableKnowledgeBases.find(kb => kb.id === kbId)?.title || 'Unknown',
          enable_filters: settings.enableFilters,
          public_access: settings.publicAccess
        };

        await chatAgentService.upsertKnowledgeBase(payload);
      }

      setSaveSuccess(true);
      await loadAgentKnowledgeBases(); // Reload to get updated data

      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      console.error('Error saving knowledge base configuration:', error);
      setSaveError(error?.response?.data?.message || error?.message || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const selectedKnowledgeBases = availableKnowledgeBases.filter(
    kb => settings.selectedKnowledgeBases.includes(kb.id)
  );

  const isLoading = isLoadingAvailable || isLoadingAgent;

  if (isLoading) {
    return (
      <div className={`h-full ${colors.background} flex items-center justify-center`}>
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className={colors.text.secondary}>Loading knowledge bases...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full ${colors.background} overflow-auto`}>
      {/* Header */}
      <div className={`px-6 py-4 border-b ${colors.border} ${colors.primary} sticky top-0 z-10`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-lg font-semibold ${colors.text.primary}`}>Knowledge Base</h1>
            <p className={`text-sm ${colors.text.muted} mt-1`}>
              Manage and configure knowledge sources for your agent
            </p>
          </div>

          <div className="flex items-center gap-3">
            {saveSuccess && (
              <div className="flex items-center gap-2 text-green-500 text-sm">
                <Check className="w-4 h-4" />
                Saved successfully
              </div>
            )}

            <button
              onClick={loadAvailableKnowledgeBases}
              className={`px-3 py-2 ${colors.secondary} border ${colors.border} hover:${colors.hover.split(' ')[0].replace('hover:', '')} text-sm rounded-md transition-colors flex items-center gap-2`}
              disabled={isLoadingAvailable}
            >
              <RefreshCw className={`w-4 h-4 ${isLoadingAvailable ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            <button
              onClick={handleSave}
              disabled={isSaving || !effectiveAgentId}
              className={`px-4 py-2 ${colors.accent} hover:bg-indigo-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white text-sm rounded-md transition-colors flex items-center gap-2`}
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Configuration
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 max-w-6xl mx-auto">

        {/* Error Message */}
        {saveError && (
          <div className={`mb-6 p-4 ${theme === 'light' ? 'bg-red-50 border-red-200' : 'bg-red-900/20 border-red-700'} border rounded-md flex items-start gap-3`}>
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className={`text-sm font-medium ${theme === 'light' ? 'text-red-800' : 'text-red-200'}`}>
                Error saving configuration
              </p>
              <p className={`text-xs ${theme === 'light' ? 'text-red-700' : 'text-red-300'} mt-1`}>
                {saveError}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left Column - Knowledge Base Selection */}
          <div className="lg:col-span-2 space-y-6">

            {/* Available Knowledge Bases */}
            <div className={`${colors.surface} rounded-lg p-6 border ${colors.border}`}>
              <h2 className={`text-lg font-semibold ${colors.text.primary} mb-4`}>
                Available Knowledge Bases
              </h2>

              <div className="space-y-4">
                <KnowledgeBaseSelector
                  availableKnowledgeBases={availableKnowledgeBases}
                  selectedKnowledgeBases={settings.selectedKnowledgeBases}
                  onKnowledgeBaseToggle={handleKnowledgeBaseToggle}
                  theme={theme}
                />

                {/* Selected Knowledge Bases Summary */}
                {selectedKnowledgeBases.length > 0 && (
                  <div className="space-y-3">
                    <h3 className={`text-sm font-medium ${colors.text.primary}`}>
                      Selected Knowledge Bases ({selectedKnowledgeBases.length})
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selectedKnowledgeBases.map((kb) => (
                        <div
                          key={kb.id}
                          className={`p-3 ${colors.secondary} border ${colors.border} rounded-md flex items-start justify-between gap-2`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <FileText className={`w-4 h-4 flex-shrink-0 ${colors.text.muted}`} />
                              <span className={`text-sm font-medium ${colors.text.primary} truncate`}>
                                {kb.title}
                              </span>
                            </div>
                            <p className={`text-xs ${colors.text.muted}`}>{kb.documentCount} documents</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setRemoveConfirmKb(kb)}
                            className={`flex-shrink-0 p-1.5 rounded-md text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors`}
                            title="Remove from selected"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Remove confirmation modal */}
                {removeConfirmKb && (
                  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div
                      className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                      onClick={() => setRemoveConfirmKb(null)}
                      aria-hidden="true"
                    />
                    <div
                      className={`relative w-full max-w-md ${colors.surface} border ${colors.border} rounded-lg shadow-xl p-6`}
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="remove-kb-title"
                      aria-describedby="remove-kb-desc"
                    >
                      <h3 id="remove-kb-title" className={`text-lg font-semibold ${colors.text.primary} mb-2`}>
                        Remove knowledge base?
                      </h3>
                      <p id="remove-kb-desc" className={`text-sm ${colors.text.secondary} mb-6`}>
                        Are you sure you want to remove <strong className={colors.text.primary}>{removeConfirmKb.title}</strong> from the selected list? You can add it again from the dropdown above.
                      </p>
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => setRemoveConfirmKb(null)}
                          className={`px-4 py-2 ${colors.secondary} border ${colors.border} rounded-md text-sm font-medium ${colors.text.primary} hover:opacity-90 transition-opacity`}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            handleKnowledgeBaseToggle(removeConfirmKb.id);
                            setRemoveConfirmKb(null);
                          }}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Right Column - Settings */}
          <div className="space-y-6">

            {/* Configuration Settings */}
            <div className={`${colors.surface} rounded-lg p-6 border ${colors.border}`}>
              <h2 className={`text-lg font-semibold ${colors.text.primary} mb-4`}>
                Configuration
              </h2>

              <div className="space-y-6">
                <ToggleSwitch
                  enabled={settings.enableFilters}
                  onChange={(value) => handleToggleChange('enableFilters', value)}
                  theme={theme}
                  label="Enable Filters"
                  description="Allow content filtering and preprocessing"
                />

                <ToggleSwitch
                  enabled={settings.publicAccess}
                  onChange={(value) => handleToggleChange('publicAccess', value)}
                  theme={theme}
                  label="Public Access"
                  description="Make knowledge base publicly accessible"
                />
              </div>
            </div>

            {/* Statistics */}
            <div className={`${colors.surface} rounded-lg p-6 border ${colors.border}`}>
              <h2 className={`text-lg font-semibold ${colors.text.primary} mb-4`}>
                Statistics
              </h2>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${colors.text.secondary}`}>Selected KBs:</span>
                  <span className={`text-sm font-medium ${colors.text.primary}`}>
                    {settings.selectedKnowledgeBases.length}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`text-sm ${colors.text.secondary}`}>Total Documents:</span>
                  <span className={`text-sm font-medium ${colors.text.primary}`}>
                    {selectedKnowledgeBases.reduce((total, kb) => total + kb.documentCount, 0)}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`text-sm ${colors.text.secondary}`}>Available KBs:</span>
                  <span className={`text-sm font-medium ${colors.text.primary}`}>
                    {availableKnowledgeBases.length}
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatAgentKnowledgeBase;
